import React, { useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import Button from "../components/Button";
import MultiSubscriptionWarningDialog from "../components/MultiSubscriptionWarningDialog";
import UserContext from "../assets/user_context";
import { openBillingPortal } from "./with_subscription";
import { useAuth } from "./use-auth";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { useTourActions, useTourState } from "../tour/TourProvider";
import { TIER_DISPLAY_NAMES } from "../config/entitlements";
import { tracedCallable } from "../utilities/tracedCallable";
import PlanCards from "../locations/account/billing/PlanCards";

// `wide` (~1100px) only when the plan cards render, so 3 plans fit
// side-by-side on desktop; on mobile the card grid stacks vertically
const ModalBox = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "wide",
})(({ theme, wide }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: wide
    ? "min(1100px, calc(100vw - 32px))"
    : "min(640px, calc(100vw - 32px))",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  boxShadow: theme.shadows[24],
  overflow: "hidden",
}));

// Soft brand-tinted band (135deg): primary tint -> secondary tint in light
// mode, muted hue equivalents in dark mode
const ModalHeader = styled(Box)(({ theme }) => ({
  background:
    theme.palette.mode === "light"
      ? "linear-gradient(135deg, #F5F9E8 0%, #E8F1F8 100%)"
      : "linear-gradient(135deg, #2c2e22 0%, #222a31 100%)",
  padding: theme.spacing(1.75, 3),
  position: "relative",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: "center",
  overflowY: "auto",
  flex: 1,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export const FreeTrialNotification = () => {
  const {
    openTrialNotification,
    setOpenTrialNotification,
    orgData,
    subscriptionData,
  } = useContext(UserContext);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const { startOnboardingTour } = useTourActions() || {};
  const tourState = useTourState();
  const hasCompletedOnboarding = tourState?.hasCompletedOnboarding ?? true;

  useEffect(() => {
    if (orgData && subscriptionData) {
      const today = new Date();

      // Check for custom expiration date first
      if (subscriptionData.freeTrialExpirationDate) {
        console.log(
          "🔍 FreeTrialNotification: Using custom expiration date:",
          subscriptionData.freeTrialExpirationDate,
        );
        const expirationDate = subscriptionData.freeTrialExpirationDate.toDate
          ? subscriptionData.freeTrialExpirationDate.toDate()
          : new Date(subscriptionData.freeTrialExpirationDate);
        const remaining = Math.max(
          0,
          Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24)),
        );
        console.log(
          "📅 FreeTrialNotification: Custom trial days remaining:",
          remaining,
        );
        setDaysRemaining(remaining);
      } else {
        // Default 15-day trial from creation date
        console.log(
          "🔍 FreeTrialNotification: Checking org creation date:",
          orgData.creationDate,
        );

        if (!orgData.creationDate) {
          console.warn(
            "⚠️ FreeTrialNotification: No creation date found for org, defaulting to 0 days remaining",
          );
          setDaysRemaining(0);
          return;
        }

        const creationDate = orgData.creationDate?.toDate();

        // Check if date is valid
        if (!creationDate || isNaN(creationDate.getTime())) {
          console.warn(
            "⚠️ FreeTrialNotification: Invalid creation date:",
            orgData.creationDate,
          );
          setDaysRemaining(0);
          return;
        }

        const daysSinceCreation = dayjs(today).diff(creationDate, "day");
        const remaining = Math.max(0, 15 - daysSinceCreation);
        console.log(
          "📅 FreeTrialNotification: Default trial - days since creation:",
          daysSinceCreation,
          "days remaining:",
          remaining,
        );
        setDaysRemaining(remaining);
      }
    } else {
      console.log(
        "🔍 FreeTrialNotification: Missing data - orgData:",
        !!orgData,
        "subscriptionData:",
        !!subscriptionData,
      );
    }
  }, [orgData, subscriptionData]);

  const handleDismiss = () => {
    setOpenTrialNotification(false);
    // After dismissing the trial modal, auto-start the onboarding tour
    if (!hasCompletedOnboarding && startOnboardingTour) {
      // Small delay so the modal close animation finishes first
      setTimeout(() => startOnboardingTour(), 500);
    }
  };

  return (
    <Modal
      open={openTrialNotification}
      onClose={handleDismiss}>
      <ModalBox>
        <ModalHeader>
          <IconButton
            onClick={handleDismiss}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "text.secondary",
            }}>
            <Close />
          </IconButton>
          <Typography variant="h4" sx={{ color: "text.primary" }}>
            Welcome to Your Free Trial! 🎉
          </Typography>
        </ModalHeader>
        <ModalContent>
          <Chip
            label={`${daysRemaining} days remaining`}
            color={daysRemaining <= 3 ? "warning" : "primary"}
            sx={{ mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            You have full access to all features during your trial period.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            After your trial ends, you'll need a subscription to continue using
            the software. No credit card required during the trial!
          </Typography>
          <Button
            variant="contained"
            onClick={handleDismiss}>
            Got it, thanks!
          </Button>
        </ModalContent>
      </ModalBox>
    </Modal>
  );
};

const SubscribeWarning = React.forwardRef(
  ({ closeModal, openSubscribeModal, closeSubscribeModal }, ref) => {
    const {
      org,
      user,
      setIsChildDataLoaded,
      openSubscribe,
      setOpenSubscribe,
      orgData,
      subscriptionData,
    } = useContext(UserContext);
    const authHook = useAuth();
    const [stripeSubscriber, setStripeSubscriber] = useState(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const [showMultiSubWarning, setShowMultiSubWarning] = useState(false);
    const [multiSubData, setMultiSubData] = useState(null);
    const [plans, setPlans] = useState(null);
    const [interval, setIntervalChoice] = useState("monthly");
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [plansError, setPlansError] = useState(null);
    const navigate = useNavigate();

    const closeSubscribe = () => {
      setOpenSubscribe(false);
    };

    // Backing out of Stripe Checkout returns to /?checkout=cancelled —
    // re-open the plan picker so they can choose a different plan.
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("checkout") === "cancelled") {
        setOpenSubscribe(true);
        params.delete("checkout"); // keep other params (e.g. org)
        const qs = params.toString();
        navigate(
          { pathname: window.location.pathname, search: qs ? `?${qs}` : "" },
          { replace: true },
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const fetchSubscriberData = async () => {
        try {
          const stripeUsersRef = collection(authHook.db, "stripeUsers");
          // User-level lookup, NOT org-scoped: the backend allows one Stripe
          // subscription per customer (extra orgs are slots), so a sub on any
          // org must surface here — an org-scoped query left stripeSubscriber
          // null on the user's other orgs and offered a checkout the backend
          // always refuses.
          const q = query(stripeUsersRef, where("userId", "==", user.uid));
          const querySnapshot = await getDocs(q);
          if (querySnapshot.docs.length === 0) {
            console.log("No stripe user found for this user");
            return;
          }
          // Prefer the active record if the user somehow has several
          const stripeUser =
            querySnapshot.docs
              .map((d) => d.data())
              .find((u) => u.subscriptionCurrent === true) ||
            querySnapshot.docs[0].data();
          console.log("stripeUser: ", stripeUser);
          setStripeSubscriber(stripeUser);

          // Check if this is a multi-subscription and fetch assigned orgs data
          if (stripeUser.subscriptionType === "multi" && stripeUser.assignedOrgs?.length > 0) {
            console.log("🔄 Fetching multi-subscription org details...");
            const assignedOrgsDetails = [];

            for (const orgId of stripeUser.assignedOrgs) {
              try {
                const orgDoc = await getDoc(doc(authHook.db, "orgs", orgId));
                if (orgDoc.exists()) {
                  const orgData = orgDoc.data();
                  // Check for Plaid connections
                  const plaidItemsSnapshot = await getDocs(
                    collection(authHook.db, "orgs", orgId, "plaidItems")
                  );
                  assignedOrgsDetails.push({
                    orgId,
                    orgName: orgData.orgName || "Unknown Organization",
                    hasPlaidConnections: !plaidItemsSnapshot.empty,
                    plaidItemCount: plaidItemsSnapshot.size,
                  });
                }
              } catch (err) {
                console.warn(`Could not fetch org ${orgId}:`, err);
                assignedOrgsDetails.push({
                  orgId,
                  orgName: "Unknown Organization",
                  hasPlaidConnections: false,
                  plaidItemCount: 0,
                });
              }
            }

            setMultiSubData({
              subscriptionType: stripeUser.subscriptionType,
              subscriptionLevel: stripeUser.subscriptionLevel,
              quantity: stripeUser.quantity || 1,
              assignedOrgs: assignedOrgsDetails,
            });
            console.log("📊 Multi-subscription data loaded:", assignedOrgsDetails.length, "orgs");
          }
        } catch (error) {
          console.error(
            "Error fetching Stripe User data: ",
            error,
            "Note this could simply be due to permissions if this user doesn't exist in the Stripe collection yet.",
          );
        }
      };
      if (user && org && !stripeSubscriber) {
        setTimeout(() => {
          fetchSubscriberData();
        }, 2000);
      }
    }, [user, org, stripeSubscriber, authHook.db, orgData]);

    // Check if user has multi-subscription
    const isMultiSubscription = stripeSubscriber?.subscriptionType === "multi" &&
      stripeSubscriber?.assignedOrgs?.length > 1;

    // Handle manage subscription click - show warning for multi-sub users
    const handleManageSubscription = () => {
      if (isMultiSubscription && multiSubData) {
        // Show warning dialog for multi-subscription users
        setShowMultiSubWarning(true);
      } else {
        // Single subscription - go directly to portal
        proceedToBillingPortal();
      }
    };

    // Proceed to billing portal
    const proceedToBillingPortal = () => {
      setShowMultiSubWarning(false);
      setPortalLoading(true);
      openBillingPortal({
        org,
        user,
        authHook,
        setLoaded: (val) => {
          setIsChildDataLoaded(val);
          if (val) setPortalLoading(false);
        },
      });
    };

    // Plans + live prices for the subscribe scenario (replaces the
    // Stripe-hosted pricing table; payment still happens on Stripe Checkout)
    useEffect(() => {
      if (!openSubscribe || stripeSubscriber || plans || !user || !org) return;
      const loadPlans = async () => {
        try {
          const getBillingOverview = tracedCallable(
            authHook.functions,
            "getBillingOverview",
          );
          const result = await getBillingOverview({ orgID: org });
          setPlans(result.data.plans || []);
        } catch (err) {
          Sentry.withScope((scope) => {
            scope.setTag("component", "SubscribeWarning");
            scope.setTag("action", "getBillingOverview");
            scope.setExtra("org_id", org);
            Sentry.captureException(err);
          });
          setPlansError("Could not load plans. Please try again.");
        }
      };
      loadPlans();
    }, [openSubscribe, stripeSubscriber, plans, user, org, authHook.functions]);

    const startCheckout = async (level) => {
      setCheckoutLoading(true);
      setPlansError(null);
      try {
        const createSubscriptionCheckout = tracedCallable(
          authHook.functions,
          "createSubscriptionCheckout",
        );
        const result = await createSubscriptionCheckout({
          orgID: org,
          level,
          interval,
        });
        // Hand off to Stripe's hosted Checkout (card, promo codes, SCA)
        window.location.assign(result.data.url);
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("component", "SubscribeWarning");
          scope.setTag("action", "createSubscriptionCheckout");
          scope.setExtra("org_id", org);
          scope.setExtra("level", level);
          scope.setExtra("interval", interval);
          Sentry.captureException(err);
        });
        setPlansError(err.message || "Could not start checkout. Please try again.");
        setCheckoutLoading(false);
      }
    };

    const isFreeTrial = orgData?.subscriptionData?.freeTrial;
    // Check if subscription is actually current (not expired)
    const hasActiveSubscription = subscriptionData?.subscriptionCurrent === true;
    // Determine if this is an upgrade scenario (active but missing features) vs expired/renewal
    const isUpgradeScenario = stripeSubscriber && hasActiveSubscription;
    // User-level sub active but THIS org isn't the subscribed one and isn't
    // assigned a slot: a second checkout would be refused by the backend
    // (one Stripe subscription per customer) — route to slot management
    // instead of plan cards.
    const orgCoveredBySub =
      stripeSubscriber?.currentOrg === org ||
      stripeSubscriber?.assignedOrgs?.includes(org);
    const needsSlotAssignment =
      stripeSubscriber?.subscriptionCurrent === true &&
      !orgCoveredBySub &&
      !hasActiveSubscription;

    // Feature-gated CTAs open the modal with { recommend, featureLabel } so
    // the plan cards highlight the tier that unlocks the feature; plain
    // setOpenSubscribe(true) callers keep the default mid-tier recommendation
    const recommendCfg =
      openSubscribe && typeof openSubscribe === "object" ? openSubscribe : {};
    const recommendKey = recommendCfg.recommend || "plus";
    const recommendBadge = recommendCfg.featureLabel || undefined;

    return (
      <>
      <Modal
        open={Boolean(openSubscribe)}
        onClose={() => {
          navigate("/");
          setTimeout(() => {
            closeSubscribe();
          }, 200);
        }}>
        <ModalBox wide={!stripeSubscriber}>
          <ModalHeader>
            <IconButton
              onClick={() => {
                navigate("/");
                setTimeout(() => {
                  closeSubscribe();
                }, 200);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "text.secondary",
              }}>
              <Close />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                color: "text.primary",
                fontWeight: "bold",
              }}>
              {isUpgradeScenario
                ? "Upgrade Your Plan"
                : needsSlotAssignment
                  ? "Add to Your Subscription"
                  : stripeSubscriber
                    ? "Renew Your Subscription"
                    : isFreeTrial
                      ? "Choose Your Plan"
                      : "Start Your Subscription"}
            </Typography>
          </ModalHeader>

          <ModalContent>
            {isUpgradeScenario ? (
              /* Active subscription but needs higher tier - show upgrade path */
              <>
                <Typography variant="h6" gutterBottom>
                  Unlock More Features 🚀
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}>
                  Upgrade to T3 {TIER_DISPLAY_NAMES.plus} for bank integration, labels, and more
                  powerful features.
                </Typography>
                {isMultiSubscription && (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ mb: 2 }}>
                    Note: You have a multi-subscription with{" "}
                    {stripeSubscriber?.assignedOrgs?.length || 0} organizations.
                    Plan changes will affect all of them.
                  </Typography>
                )}
                <Button
                  variant="contained"
                  disabled={portalLoading}
                  onClick={() => {
                    // Active subscription: plan changes happen in-app now.
                    navigate("/account/billing");
                    closeSubscribe();
                  }}>
                  Manage Subscription
                </Button>
              </>
            ) : needsSlotAssignment ? (
              /* Active sub on another org — this one needs a slot, not a
                 second checkout (the backend refuses those) */
              <>
                <Typography variant="h6" gutterBottom>
                  Add this organization to your subscription
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}>
                  Your account already has an active subscription. Instead of
                  checking out again, add a slot for this organization from
                  Subscription Management.
                </Typography>
                <Button
                  variant="contained"
                  data-testid="subscribe-modal-manage-slots"
                  onClick={() => {
                    navigate("/account/subscriptions");
                    closeSubscribe();
                  }}>
                  Manage Subscriptions
                </Button>
              </>
            ) : stripeSubscriber ? (
              /* Has Stripe record but subscription expired - show renewal */
              <>
                <Typography variant="h6" gutterBottom>
                  Your subscription has expired 😔
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}>
                  Renew your subscription to continue using all features of the
                  app.
                </Typography>
                <Button
                  variant="contained"
                  disabled={portalLoading}
                  onClick={handleManageSubscription}>
                  {portalLoading ? "Loading..." : "Manage Subscription"}
                </Button>
              </>
            ) : (
              <>
                {plansError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2, textAlign: "left" }}
                    onClose={() => setPlansError(null)}>
                    {plansError}
                  </Alert>
                )}
                {plans ? (
                  <Box sx={{ mb: 1 }}>
                    <PlanCards
                      plans={plans}
                      interval={interval}
                      onIntervalChange={setIntervalChoice}
                      onSelect={startCheckout}
                      disabled={checkoutLoading}
                      recommendedKey={recommendKey}
                      recommendBadge={recommendBadge}
                    />
                    {checkoutLoading && (
                      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, gap: 1, alignItems: "center" }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Taking you to secure checkout…
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  !plansError && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress />
                    </Box>
                  )
                )}
              </>
            )}
          </ModalContent>
        </ModalBox>
      </Modal>

      {/* Multi-subscription warning dialog */}
      <MultiSubscriptionWarningDialog
        open={showMultiSubWarning}
        onClose={() => setShowMultiSubWarning(false)}
        onConfirm={proceedToBillingPortal}
        processing={portalLoading}
        assignedOrgs={multiSubData?.assignedOrgs || []}
        subscriptionLevel={multiSubData?.subscriptionLevel || "basic"}
        quantity={multiSubData?.quantity || 1}
      />
      </>
    );
  },
);

export default SubscribeWarning;
